"use client";

import { useState, useEffect, useCallback } from "react";
import { User } from "firebase/auth";
import { onAuthChange, signInWithGoogle, signOut, consumeRedirectResult } from "@/lib/firebase/auth";
import { apiFetch } from "@/lib/api/client";
import type { UserData } from "@/lib/types/user";

interface AuthState {
  firebaseUser: User | null;
  userData: UserData | null;
  loading: boolean;
}

export function useAuth() {
  const [state, setState] = useState<AuthState>({
    firebaseUser: null,
    userData: null,
    loading: true,
  });

  useEffect(() => {
    consumeRedirectResult().catch((err) => {
      console.error("[auth] getRedirectResult failed:", err);
    });

    const unsubscribe = onAuthChange(async (user) => {
      if (user) {
        try {
          const token = await user.getIdToken();
          const data = await apiFetch<UserData>("/api/user", token, {
            method: "POST",
            body: { displayName: user.displayName || "名無しの旅人" },
          });
          setState({ firebaseUser: user, userData: data, loading: false });
        } catch (err) {
          console.error("[auth] /api/user POST failed:", err);
          setState({ firebaseUser: null, userData: null, loading: false });
        }
      } else {
        setState({ firebaseUser: null, userData: null, loading: false });
      }
    });
    return unsubscribe;
  }, []);

  const login = useCallback(async () => {
    await signInWithGoogle();
  }, []);

  const logout = useCallback(async () => {
    await signOut();
  }, []);

  /** userData を再取得する（パートナー変更など、サーバー側の変更をクライアントに反映したい時に使う） */
  const reloadUserData = useCallback(async () => {
    if (!state.firebaseUser) return;
    try {
      const token = await state.firebaseUser.getIdToken();
      const data = await apiFetch<UserData>("/api/user", token, {
        method: "POST",
        body: { displayName: state.firebaseUser.displayName || "名無しの旅人" },
      });
      setState((s) => ({ ...s, userData: data }));
    } catch { /* ignore */ }
  }, [state.firebaseUser]);

  return {
    user: state.firebaseUser,
    userData: state.userData,
    loading: state.loading,
    isLoggedIn: !!state.firebaseUser,
    login,
    logout,
    reloadUserData,
  };
}
